import { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X, Copy, Check, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useSite } from "@/context/SiteContext";

const CODE = "ALLSTAR27";
const KEY = "tca_promo_allstar27_seen";
const SKIP = ["/admin", "/portal", "/login", "/reset-password"];
const PROMO_SRC = "/brand/promo-allstar27.webp";

async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
  } catch (e) {
    const ta = document.createElement("textarea");
    ta.value = text;
    document.body.appendChild(ta);
    ta.select();
    document.execCommand("copy");
    ta.remove();
  }
}

const RegisterBtn = ({ url, onClick, hotspot }) => {
  const cls = hotspot
    ? "absolute left-[32.5%] top-[78.8%] h-[5.8%] w-[35.5%] focus-visible:outline focus-visible:outline-2 focus-visible:outline-white"
    : "btn-gold !px-6 !py-3 !text-base w-full sm:w-auto";
  const testid = hotspot ? "promo-graphic-register-hotspot" : "promo-register-button";
  const label = hotspot ? <span className="sr-only">Register now</span> : <>Register Now <ArrowRight className="h-4 w-4" /></>;
  if (url) {
    return <a href={url} target="_blank" rel="noreferrer" onClick={onClick} className={cls} data-testid={testid}>{label}</a>;
  }
  return <Link to="/tryouts" onClick={onClick} className={cls} data-testid={testid}>{label}</Link>;
};

export const PromoPopup = () => {
  const { pathname } = useLocation();
  const { settings } = useSite();
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const skip = SKIP.some((p) => pathname.startsWith(p));

  useEffect(() => {
    if (skip || sessionStorage.getItem(KEY)) return undefined;
    let alive = true;
    const img = new Image();
    const ready = new Promise((res) => {
      img.onload = () => res(true);
      img.onerror = () => res(false);
    });
    img.src = PROMO_SRC;
    const t = setTimeout(async () => {
      if (!(await ready) || !alive) return;
      sessionStorage.setItem(KEY, "1");
      setOpen(true);
    }, 2000);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [skip]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const copy = async () => {
    await copyText(CODE);
    setCopied(true);
    toast.success(`Promo code ${CODE} copied!`);
    setTimeout(() => setCopied(false), 2500);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/75 backdrop-blur-md p-4"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}
          onClick={() => setOpen(false)}
          role="dialog" aria-modal="true" aria-label="ALLSTAR27 promotion"
          data-testid="promo-popup-overlay"
        >
          <motion.div
            className="relative flex flex-col items-center gap-3"
            initial={{ scale: 0.6, opacity: 0, y: 30 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 20 }}
            onClick={(e) => e.stopPropagation()}
            data-testid="promo-popup"
          >
            <div className="relative">
              <div className="absolute -inset-1 bg-gold/40 blur-2xl" aria-hidden="true" />
              <img
                src={PROMO_SRC}
                alt="Tri County Allstars Combine promotion — use code ALLSTAR27"
                className="relative block h-auto w-auto max-w-[min(92vw,560px)] max-h-[calc(100svh-170px)] object-contain shadow-[0_30px_80px_rgba(0,0,0,0.7)] ring-2 ring-gold/70"
                data-testid="promo-popup-image"
              />
              <button onClick={() => setOpen(false)} aria-label="Close promotion" className="absolute left-[91.5%] top-[1.2%] h-[7%] w-[7.2%]" data-testid="promo-graphic-close-hotspot" />
              <button onClick={copy} aria-label="Copy promo code ALLSTAR27" className="absolute left-[16%] top-[52%] h-[18.5%] w-[75%]" data-testid="promo-graphic-code-hotspot" />
              <RegisterBtn hotspot url={settings.eventbrite_url} onClick={() => setOpen(false)} />
              <button onClick={() => setOpen(false)} aria-label="Close promotion"
                className="absolute -right-3 -top-3 flex h-11 w-11 items-center justify-center rounded-full bg-white text-navy shadow-lg ring-2 ring-gold transition-transform hover:scale-110"
                data-testid="promo-popup-close">
                <X className="h-6 w-6" strokeWidth={3} />
              </button>
            </div>
            <div className="flex w-full max-w-[min(92vw,560px)] flex-col sm:flex-row items-stretch gap-2">
              <button onClick={copy} className="flex flex-1 items-center justify-between gap-2 border-2 border-dashed border-gold bg-pitch/90 px-4 py-2.5 transition-colors hover:bg-navy" data-testid="promo-copy-code">
                <span className="font-display text-2xl font-black italic tracking-wider text-gold">{CODE}</span>
                <span className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-white">
                  {copied ? <><Check className="h-4 w-4 text-emerald-400" /> Copied</> : <><Copy className="h-4 w-4" /> Copy Code</>}
                </span>
              </button>
              <RegisterBtn url={settings.eventbrite_url} onClick={() => setOpen(false)} />
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
